/*
 * Name-number computation lives in the backend engine (Chaldean) and is fetched
 * via `calculateNameNumber` from `@/lib/api`. The client-side Pythagorean
 * helpers that used to live here (letterValue / reduceToSingle /
 * calculateNameNumber) were removed 2026-06-02 — one source of truth (rule 29).
 */

export interface NumberProfile {
  number: number;
  planet: string;
  planet_hi: string;
  title: string;
  characteristics: string[];
  strengths: string[];
  weaknesses: string[];
  compatible: number[];
  incompatible: number[];
  lucky_color: string;
  lucky_day: string;
  career: string[];
}

export const PROFILES: NumberProfile[] = [
  {
    number: 1,
    planet: "Sun (Surya)",
    planet_hi: "सूर्य",
    title: "The Leader",
    characteristics: [
      "Natural born leader with strong willpower",
      "Independent, original, and ambitious",
      "Pioneer who forges new paths",
      "Strong sense of self and identity",
    ],
    strengths: ["Leadership", "Determination", "Creativity", "Confidence"],
    weaknesses: ["Stubbornness", "Impatience", "Ego-driven decisions", "Difficulty taking advice"],
    compatible: [1, 3, 5, 9],
    incompatible: [2, 4, 8],
    lucky_color: "Gold / Orange",
    lucky_day: "Sunday",
    career: [
      "Entrepreneurship",
      "Politics",
      "Management",
      "Creative Direction",
      "Government Service",
    ],
  },
  {
    number: 2,
    planet: "Moon (Chandra)",
    planet_hi: "चंद्रमा",
    title: "The Diplomat",
    characteristics: [
      "Sensitive, intuitive, and empathetic",
      "Natural peacemaker and mediator",
      "Cooperative and supportive partner",
      "Strong emotional intelligence",
    ],
    strengths: ["Diplomacy", "Intuition", "Patience", "Cooperation"],
    weaknesses: ["Over-sensitivity", "Indecisiveness", "Dependency on others", "Mood swings"],
    compatible: [1, 2, 4, 7],
    incompatible: [5, 8, 9],
    lucky_color: "White / Silver",
    lucky_day: "Monday",
    career: ["Counseling", "Teaching", "Nursing", "Diplomacy", "Art & Music"],
  },
  {
    number: 3,
    planet: "Jupiter (Guru)",
    planet_hi: "गुरु (बृहस्पति)",
    title: "The Communicator",
    characteristics: [
      "Expressive, optimistic, and social",
      "Natural entertainer and storyteller",
      "Creative mind with diverse talents",
      "Attracts people with charisma",
    ],
    strengths: ["Communication", "Optimism", "Creativity", "Charisma"],
    weaknesses: ["Scattered energy", "Superficiality", "Extravagance", "Difficulty with focus"],
    compatible: [1, 3, 5, 6, 9],
    incompatible: [4, 7, 8],
    lucky_color: "Yellow / Purple",
    lucky_day: "Thursday",
    career: ["Writing", "Acting", "Marketing", "Teaching", "Public Speaking"],
  },
  {
    number: 4,
    planet: "Rahu",
    planet_hi: "राहु",
    title: "The Builder",
    characteristics: [
      "Practical, disciplined, and methodical",
      "Strong work ethic and persistence",
      "Unconventional approach to problems",
      "Values stability and structure",
    ],
    strengths: ["Discipline", "Reliability", "Practicality", "Persistence"],
    weaknesses: ["Rigidity", "Stubbornness", "Resistance to change", "Pessimism"],
    compatible: [2, 4, 6, 8],
    incompatible: [1, 3, 5, 9],
    lucky_color: "Blue / Grey",
    lucky_day: "Saturday",
    career: ["Engineering", "Architecture", "Technology", "Research", "Banking"],
  },
  {
    number: 5,
    planet: "Mercury (Budh)",
    planet_hi: "बुध",
    title: "The Explorer",
    characteristics: [
      "Adventurous, versatile, and quick-witted",
      "Love for freedom and new experiences",
      "Natural communicator and negotiator",
      "Adaptable to any situation",
    ],
    strengths: ["Adaptability", "Communication", "Versatility", "Quick learning"],
    weaknesses: ["Restlessness", "Inconsistency", "Risk-taking", "Difficulty with commitment"],
    compatible: [1, 3, 5, 7, 9],
    incompatible: [2, 4, 6],
    lucky_color: "Green / Turquoise",
    lucky_day: "Wednesday",
    career: ["Business", "Travel Industry", "Media", "Sales", "Consulting"],
  },
  {
    number: 6,
    planet: "Venus (Shukra)",
    planet_hi: "शुक्र",
    title: "The Nurturer",
    characteristics: [
      "Loving, responsible, and harmonious",
      "Strong sense of family and duty",
      "Artistic taste and aesthetic sense",
      "Natural caretaker and healer",
    ],
    strengths: ["Compassion", "Responsibility", "Artistic sense", "Harmony"],
    weaknesses: ["Self-sacrifice", "Worry", "Possessiveness", "Perfectionism"],
    compatible: [3, 6, 7, 9],
    incompatible: [1, 4, 5, 8],
    lucky_color: "Pink / Light Blue",
    lucky_day: "Friday",
    career: ["Healthcare", "Interior Design", "Hospitality", "Fashion", "Social Work"],
  },
  {
    number: 7,
    planet: "Ketu",
    planet_hi: "केतु",
    title: "The Seeker",
    characteristics: [
      "Analytical, spiritual, and introspective",
      "Deep thinker who questions everything",
      "Drawn to mysteries and hidden knowledge",
      "Values solitude and inner wisdom",
    ],
    strengths: ["Analytical mind", "Spirituality", "Intuition", "Research ability"],
    weaknesses: ["Isolation", "Aloofness", "Skepticism", "Difficulty expressing emotions"],
    compatible: [2, 5, 6, 7],
    incompatible: [1, 3, 8, 9],
    lucky_color: "Violet / White",
    lucky_day: "Monday",
    career: ["Research", "Philosophy", "Spirituality", "Psychology", "Data Science"],
  },
  {
    number: 8,
    planet: "Saturn (Shani)",
    planet_hi: "शनि",
    title: "The Achiever",
    characteristics: [
      "Ambitious, authoritative, and strategic",
      "Strong material drive and business sense",
      "Natural executive and organizer",
      "Karmic lessons through power and wealth",
    ],
    strengths: ["Ambition", "Organization", "Business acumen", "Perseverance"],
    weaknesses: ["Workaholism", "Materialism", "Controlling nature", "Difficulty with emotions"],
    compatible: [2, 4, 6, 8],
    incompatible: [1, 3, 5, 9],
    lucky_color: "Black / Dark Blue",
    lucky_day: "Saturday",
    career: ["Finance", "Real Estate", "Law", "Corporate Management", "Politics"],
  },
  {
    number: 9,
    planet: "Mars (Mangal)",
    planet_hi: "मंगल",
    title: "The Warrior",
    characteristics: [
      "Courageous, energetic, and passionate",
      "Natural fighter for causes and justice",
      "Generous and humanitarian spirit",
      "Strong physical and mental stamina",
    ],
    strengths: ["Courage", "Generosity", "Leadership", "Passion"],
    weaknesses: ["Aggression", "Impatience", "Impulsiveness", "Emotional volatility"],
    compatible: [1, 3, 5, 6, 9],
    incompatible: [2, 4, 7, 8],
    lucky_color: "Red / Maroon",
    lucky_day: "Tuesday",
    career: ["Military/Defense", "Sports", "Surgery", "Social Activism", "Entrepreneurship"],
  },
];
